// Frame <-> timestamp helpers for restored analyses.
//
// Analyses store metrics keyed by SAM2 backend frame index (oldest-first). The
// dashboard reads a persisted mapping (Analysis/{sid}/frame_timestamps.json,
// also copied into each save snapshot) shaped like the live /frame_info payload:
//   { frame_timestamps: { "0": 0.0, "1": 2.3, ... }, order: "oldest_first", ... }
// where the values are seconds elapsed since the first frame. For an experiment
// that was merged across a power outage, the elapsed values carry the real gap,
// so any time axis can break across the dead window instead of interpolating.

// Elapsed seconds for a backend frame index, or null if unknown.
export function elapsedSecondsFor(frameTimestamps, frameIdx) {
  if (!frameTimestamps) return null;
  const v = frameTimestamps[frameIdx] ?? frameTimestamps[String(frameIdx)];
  return typeof v === 'number' && !Number.isNaN(v) ? v : null;
}

// Compact elapsed label, e.g. 0s, 45m, 3h 20m, 91h.
export function formatElapsed(seconds) {
  if (seconds == null || Number.isNaN(seconds)) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return `${m}m`;
  return `${Math.floor(seconds)}s`;
}

// Build a category time-axis for an ordered set of frame indices.
//
// Wherever the gap between two consecutive frames is far larger than the typical
// interval (gapFactor x the median), a break slot is inserted: label '', frame
// null. Callers map a null frame to a null data value with spanGaps:false so the
// line renders an actual gap across the lost time (the NaN the outage deserves).
//
// When no usable timestamps are present (frameTimestamps missing/empty), falls
// back to "Frame N" labels and no breaks, i.e. the previous behaviour.
export function buildTimeAxis(frameIndices, frameTimestamps, { gapFactor = 4 } = {}) {
  const frames = [...new Set(frameIndices)].sort((a, b) => a - b);
  const elapsed = frames.map((f) => elapsedSecondsFor(frameTimestamps, f));
  const haveTimes = elapsed.some((e) => e != null);

  if (!haveTimes) {
    return {
      hasTime: false,
      labels: frames.map((f) => `Frame ${f}`),
      frames: [...frames],
      axisTitle: 'Frame',
    };
  }

  const diffs = [];
  for (let i = 1; i < elapsed.length; i++) {
    if (elapsed[i] != null && elapsed[i - 1] != null) diffs.push(elapsed[i] - elapsed[i - 1]);
  }
  const sorted = [...diffs].sort((a, b) => a - b);
  const median = sorted.length ? sorted[Math.floor(sorted.length / 2)] : 0;
  const threshold = median > 0 ? median * gapFactor : Infinity;

  const labels = [];
  const outFrames = [];
  for (let i = 0; i < frames.length; i++) {
    if (
      i > 0 &&
      elapsed[i] != null &&
      elapsed[i - 1] != null &&
      elapsed[i] - elapsed[i - 1] > threshold
    ) {
      labels.push(''); // outage break slot
      outFrames.push(null);
    }
    labels.push(elapsed[i] != null ? formatElapsed(elapsed[i]) : `Frame ${frames[i]}`);
    outFrames.push(frames[i]);
  }
  return { hasTime: true, labels, frames: outFrames, axisTitle: 'Time (elapsed)' };
}
